/**
 * MobileNumberInput.tsx
 * Premium cross-platform mobile number input with:
 *  - ALL countries with emoji flags (150+)
 *  - Per-country digit length validation
 *  - Separate country code box + gap + phone input box
 *  - Red border on blur if invalid, Blue when focused
 *  - Shake animation on invalid
 */

import React, { useState, useRef } from 'react';
import {
  View,
  Text,
  TextInput,
  TouchableOpacity,
  FlatList,
  StyleSheet,
  Platform,
  Animated,
  Modal,
  Dimensions,
} from 'react-native';
import OurIcon from './icon';
import { useTheme } from '../context/themeContext';

// ─────────────────────────────────────
//  Country Data — 150+ countries
// ─────────────────────────────────────

export interface CountryEntry {
  readonly code: string;
  readonly name: string;
  readonly dialCode: string;
  readonly flag: string;
  readonly digitCount: number;
}

export interface MobileNumberInputProps {
  readonly label?: string;
  readonly required?: boolean;
  readonly value?: string;
  readonly selectedCountry: CountryEntry;
  readonly onCountryChange: (country: CountryEntry) => void;
  readonly onChangeText: (text: string) => void;
  readonly onBlur?: () => void;
  readonly placeholder?: string;
  readonly editable?: boolean;
  readonly error?: string;
  /** Called when a non-digit keystroke is blocked (lets the host show a toast). */
  readonly onInvalidChar?: () => void;
  readonly data?: any;
}

export const COUNTRIES: CountryEntry[] = [
  { code: 'AF', name: 'Afghanistan',          dialCode: '+93',   flag: '🇦🇫', digitCount: 9  },
  { code: 'AL', name: 'Albania',              dialCode: '+355',  flag: '🇦🇱', digitCount: 9  },
  { code: 'DZ', name: 'Algeria',              dialCode: '+213',  flag: '🇩🇿', digitCount: 9  },
  { code: 'AD', name: 'Andorra',              dialCode: '+376',  flag: '🇦🇩', digitCount: 6  },
  { code: 'AO', name: 'Angola',               dialCode: '+244',  flag: '🇦🇴', digitCount: 9  },
  { code: 'AR', name: 'Argentina',            dialCode: '+54',   flag: '🇦🇷', digitCount: 10 },
  { code: 'AM', name: 'Armenia',              dialCode: '+374',  flag: '🇦🇲', digitCount: 8  },
  { code: 'AU', name: 'Australia',            dialCode: '+61',   flag: '🇦🇺', digitCount: 9  },
  { code: 'AT', name: 'Austria',              dialCode: '+43',   flag: '🇦🇹', digitCount: 10 },
  { code: 'AZ', name: 'Azerbaijan',           dialCode: '+994',  flag: '🇦🇿', digitCount: 9  },
  { code: 'BS', name: 'Bahamas',              dialCode: '+1242', flag: '🇧🇸', digitCount: 7  },
  { code: 'BH', name: 'Bahrain',              dialCode: '+973',  flag: '🇧🇭', digitCount: 8  },
  { code: 'BD', name: 'Bangladesh',           dialCode: '+880',  flag: '🇧🇩', digitCount: 10 },
  { code: 'BB', name: 'Barbados',             dialCode: '+1246', flag: '🇧🇧', digitCount: 7  },
  { code: 'BY', name: 'Belarus',              dialCode: '+375',  flag: '🇧🇾', digitCount: 9  },
  { code: 'BE', name: 'Belgium',              dialCode: '+32',   flag: '🇧🇪', digitCount: 9  },
  { code: 'BZ', name: 'Belize',               dialCode: '+501',  flag: '🇧🇿', digitCount: 7  },
  { code: 'BJ', name: 'Benin',                dialCode: '+229',  flag: '🇧🇯', digitCount: 8  },
  { code: 'BT', name: 'Bhutan',               dialCode: '+975',  flag: '🇧🇹', digitCount: 8  },
  { code: 'BO', name: 'Bolivia',              dialCode: '+591',  flag: '🇧🇴', digitCount: 8  },
  { code: 'BA', name: 'Bosnia & Herzegovina', dialCode: '+387',  flag: '🇧🇦', digitCount: 8  },
  { code: 'BW', name: 'Botswana',             dialCode: '+267',  flag: '🇧🇼', digitCount: 8  },
  { code: 'BR', name: 'Brazil',               dialCode: '+55',   flag: '🇧🇷', digitCount: 11 },
  { code: 'BN', name: 'Brunei',               dialCode: '+673',  flag: '🇧🇳', digitCount: 7  },
  { code: 'BG', name: 'Bulgaria',             dialCode: '+359',  flag: '🇧🇬', digitCount: 9  },
  { code: 'BF', name: 'Burkina Faso',         dialCode: '+226',  flag: '🇧🇫', digitCount: 8  },
  { code: 'BI', name: 'Burundi',              dialCode: '+257',  flag: '🇧🇮', digitCount: 8  },
  { code: 'KH', name: 'Cambodia',             dialCode: '+855',  flag: '🇰🇭', digitCount: 9  },
  { code: 'CM', name: 'Cameroon',             dialCode: '+237',  flag: '🇨🇲', digitCount: 9  },
  { code: 'CA', name: 'Canada',               dialCode: '+1',    flag: '🇨🇦', digitCount: 10 },
  { code: 'CV', name: 'Cape Verde',           dialCode: '+238',  flag: '🇨🇻', digitCount: 7  },
  { code: 'CF', name: 'Central African Rep.', dialCode: '+236',  flag: '🇨🇫', digitCount: 8  },
  { code: 'TD', name: 'Chad',                 dialCode: '+235',  flag: '🇹🇩', digitCount: 8  },
  { code: 'CL', name: 'Chile',                dialCode: '+56',   flag: '🇨🇱', digitCount: 9  },
  { code: 'CN', name: 'China',                dialCode: '+86',   flag: '🇨🇳', digitCount: 11 },
  { code: 'CO', name: 'Colombia',             dialCode: '+57',   flag: '🇨🇴', digitCount: 10 },
  { code: 'KM', name: 'Comoros',              dialCode: '+269',  flag: '🇰🇲', digitCount: 7  },
  { code: 'CG', name: 'Congo',                dialCode: '+242',  flag: '🇨🇬', digitCount: 9  },
  { code: 'CR', name: 'Costa Rica',           dialCode: '+506',  flag: '🇨🇷', digitCount: 8  },
  { code: 'HR', name: 'Croatia',              dialCode: '+385',  flag: '🇭🇷', digitCount: 9  },
  { code: 'CU', name: 'Cuba',                 dialCode: '+53',   flag: '🇨🇺', digitCount: 8  },
  { code: 'CY', name: 'Cyprus',               dialCode: '+357',  flag: '🇨🇾', digitCount: 8  },
  { code: 'CZ', name: 'Czech Republic',       dialCode: '+420',  flag: '🇨🇿', digitCount: 9  },
  { code: 'DK', name: 'Denmark',              dialCode: '+45',   flag: '🇩🇰', digitCount: 8  },
  { code: 'DJ', name: 'Djibouti',             dialCode: '+253',  flag: '🇩🇯', digitCount: 8  },
  { code: 'DO', name: 'Dominican Republic',   dialCode: '+1809', flag: '🇩🇴', digitCount: 7  },
  { code: 'EC', name: 'Ecuador',              dialCode: '+593',  flag: '🇪🇨', digitCount: 9  },
  { code: 'EG', name: 'Egypt',                dialCode: '+20',   flag: '🇪🇬', digitCount: 10 },
  { code: 'SV', name: 'El Salvador',          dialCode: '+503',  flag: '🇸🇻', digitCount: 8  },
  { code: 'GQ', name: 'Equatorial Guinea',    dialCode: '+240',  flag: '🇬🇶', digitCount: 9  },
  { code: 'ER', name: 'Eritrea',              dialCode: '+291',  flag: '🇪🇷', digitCount: 7  },
  { code: 'EE', name: 'Estonia',              dialCode: '+372',  flag: '🇪🇪', digitCount: 8  },
  { code: 'ET', name: 'Ethiopia',             dialCode: '+251',  flag: '🇪🇹', digitCount: 9  },
  { code: 'FJ', name: 'Fiji',                 dialCode: '+679',  flag: '🇫🇯', digitCount: 7  },
  { code: 'FI', name: 'Finland',              dialCode: '+358',  flag: '🇫🇮', digitCount: 9  },
  { code: 'FR', name: 'France',               dialCode: '+33',   flag: '🇫🇷', digitCount: 9  },
  { code: 'GA', name: 'Gabon',                dialCode: '+241',  flag: '🇬🇦', digitCount: 8  },
  { code: 'GM', name: 'Gambia',               dialCode: '+220',  flag: '🇬🇲', digitCount: 7  },
  { code: 'GE', name: 'Georgia',              dialCode: '+995',  flag: '🇬🇪', digitCount: 9  },
  { code: 'DE', name: 'Germany',              dialCode: '+49',   flag: '🇩🇪', digitCount: 10 },
  { code: 'GH', name: 'Ghana',                dialCode: '+233',  flag: '🇬🇭', digitCount: 9  },
  { code: 'GR', name: 'Greece',               dialCode: '+30',   flag: '🇬🇷', digitCount: 10 },
  { code: 'GT', name: 'Guatemala',            dialCode: '+502',  flag: '🇬🇹', digitCount: 8  },
  { code: 'GN', name: 'Guinea',               dialCode: '+224',  flag: '🇬🇳', digitCount: 9  },
  { code: 'GW', name: 'Guinea-Bissau',        dialCode: '+245',  flag: '🇬🇼', digitCount: 7  },
  { code: 'GY', name: 'Guyana',               dialCode: '+592',  flag: '🇬🇾', digitCount: 7  },
  { code: 'HT', name: 'Haiti',                dialCode: '+509',  flag: '🇭🇹', digitCount: 8  },
  { code: 'HN', name: 'Honduras',             dialCode: '+504',  flag: '🇭🇳', digitCount: 8  },
  { code: 'HK', name: 'Hong Kong',            dialCode: '+852',  flag: '🇭🇰', digitCount: 8  },
  { code: 'HU', name: 'Hungary',              dialCode: '+36',   flag: '🇭🇺', digitCount: 9  },
  { code: 'IS', name: 'Iceland',              dialCode: '+354',  flag: '🇮🇸', digitCount: 7  },
  { code: 'IN', name: 'India',                dialCode: '+91',   flag: '🇮🇳', digitCount: 10 },
  { code: 'ID', name: 'Indonesia',            dialCode: '+62',   flag: '🇮🇩', digitCount: 11 },
  { code: 'IR', name: 'Iran',                 dialCode: '+98',   flag: '🇮🇷', digitCount: 10 },
  { code: 'IQ', name: 'Iraq',                 dialCode: '+964',  flag: '🇮🇶', digitCount: 10 },
  { code: 'IE', name: 'Ireland',              dialCode: '+353',  flag: '🇮🇪', digitCount: 9  },
  { code: 'IL', name: 'Israel',               dialCode: '+972',  flag: '🇮🇱', digitCount: 9  },
  { code: 'IT', name: 'Italy',                dialCode: '+39',   flag: '🇮🇹', digitCount: 10 },
  { code: 'CI', name: "Ivory Coast",          dialCode: '+225',  flag: '🇨🇮', digitCount: 8  },
  { code: 'JM', name: 'Jamaica',              dialCode: '+1876', flag: '🇯🇲', digitCount: 7  },
  { code: 'JP', name: 'Japan',                dialCode: '+81',   flag: '🇯🇵', digitCount: 10 },
  { code: 'JO', name: 'Jordan',               dialCode: '+962',  flag: '🇯🇴', digitCount: 9  },
  { code: 'KZ', name: 'Kazakhstan',           dialCode: '+7',    flag: '🇰🇿', digitCount: 10 },
  { code: 'KE', name: 'Kenya',                dialCode: '+254',  flag: '🇰🇪', digitCount: 9  },
  { code: 'KI', name: 'Kiribati',             dialCode: '+686',  flag: '🇰🇮', digitCount: 5  },
  { code: 'KP', name: 'North Korea',          dialCode: '+850',  flag: '🇰🇵', digitCount: 9  },
  { code: 'KR', name: 'South Korea',          dialCode: '+82',   flag: '🇰🇷', digitCount: 10 },
  { code: 'KW', name: 'Kuwait',               dialCode: '+965',  flag: '🇰🇼', digitCount: 8  },
  { code: 'KG', name: 'Kyrgyzstan',           dialCode: '+996',  flag: '🇰🇬', digitCount: 9  },
  { code: 'LA', name: 'Laos',                 dialCode: '+856',  flag: '🇱🇦', digitCount: 9  },
  { code: 'LV', name: 'Latvia',               dialCode: '+371',  flag: '🇱🇻', digitCount: 8  },
  { code: 'LB', name: 'Lebanon',              dialCode: '+961',  flag: '🇱🇧', digitCount: 8  },
  { code: 'LS', name: 'Lesotho',              dialCode: '+266',  flag: '🇱🇸', digitCount: 8  },
  { code: 'LR', name: 'Liberia',              dialCode: '+231',  flag: '🇱🇷', digitCount: 8  },
  { code: 'LY', name: 'Libya',                dialCode: '+218',  flag: '🇱🇾', digitCount: 9  },
  { code: 'LI', name: 'Liechtenstein',        dialCode: '+423',  flag: '🇱🇮', digitCount: 7  },
  { code: 'LT', name: 'Lithuania',            dialCode: '+370',  flag: '🇱🇹', digitCount: 8  },
  { code: 'LU', name: 'Luxembourg',           dialCode: '+352',  flag: '🇱🇺', digitCount: 9  },
  { code: 'MO', name: 'Macao',                dialCode: '+853',  flag: '🇲🇴', digitCount: 8  },
  { code: 'MG', name: 'Madagascar',           dialCode: '+261',  flag: '🇲🇬', digitCount: 9  },
  { code: 'MW', name: 'Malawi',               dialCode: '+265',  flag: '🇲🇼', digitCount: 9  },
  { code: 'MY', name: 'Malaysia',             dialCode: '+60',   flag: '🇲🇾', digitCount: 10 },
  { code: 'MV', name: 'Maldives',             dialCode: '+960',  flag: '🇲🇻', digitCount: 7  },
  { code: 'ML', name: 'Mali',                 dialCode: '+223',  flag: '🇲🇱', digitCount: 8  },
  { code: 'MT', name: 'Malta',                dialCode: '+356',  flag: '🇲🇹', digitCount: 8  },
  { code: 'MR', name: 'Mauritania',           dialCode: '+222',  flag: '🇲🇷', digitCount: 8  },
  { code: 'MU', name: 'Mauritius',            dialCode: '+230',  flag: '🇲🇺', digitCount: 8  },
  { code: 'MX', name: 'Mexico',               dialCode: '+52',   flag: '🇲🇽', digitCount: 10 },
  { code: 'FM', name: 'Micronesia',           dialCode: '+691',  flag: '🇫🇲', digitCount: 7  },
  { code: 'MD', name: 'Moldova',              dialCode: '+373',  flag: '🇲🇩', digitCount: 8  },
  { code: 'MC', name: 'Monaco',               dialCode: '+377',  flag: '🇲🇨', digitCount: 8  },
  { code: 'MN', name: 'Mongolia',             dialCode: '+976',  flag: '🇲🇳', digitCount: 8  },
  { code: 'ME', name: 'Montenegro',           dialCode: '+382',  flag: '🇲🇪', digitCount: 8  },
  { code: 'MA', name: 'Morocco',              dialCode: '+212',  flag: '🇲🇦', digitCount: 9  },
  { code: 'MZ', name: 'Mozambique',           dialCode: '+258',  flag: '🇲🇿', digitCount: 9  },
  { code: 'MM', name: 'Myanmar',              dialCode: '+95',   flag: '🇲🇲', digitCount: 9  },
  { code: 'NA', name: 'Namibia',              dialCode: '+264',  flag: '🇳🇦', digitCount: 9  },
  { code: 'NP', name: 'Nepal',                dialCode: '+977',  flag: '🇳🇵', digitCount: 10 },
  { code: 'NL', name: 'Netherlands',          dialCode: '+31',   flag: '🇳🇱', digitCount: 9  },
  { code: 'NZ', name: 'New Zealand',          dialCode: '+64',   flag: '🇳🇿', digitCount: 9  },
  { code: 'NI', name: 'Nicaragua',            dialCode: '+505',  flag: '🇳🇮', digitCount: 8  },
  { code: 'NE', name: 'Niger',                dialCode: '+227',  flag: '🇳🇪', digitCount: 8  },
  { code: 'NG', name: 'Nigeria',              dialCode: '+234',  flag: '🇳🇬', digitCount: 10 },
  { code: 'MK', name: 'North Macedonia',      dialCode: '+389',  flag: '🇲🇰', digitCount: 8  },
  { code: 'NO', name: 'Norway',               dialCode: '+47',   flag: '🇳🇴', digitCount: 8  },
  { code: 'OM', name: 'Oman',                 dialCode: '+968',  flag: '🇴🇲', digitCount: 8  },
  { code: 'PK', name: 'Pakistan',             dialCode: '+92',   flag: '🇵🇰', digitCount: 10 },
  { code: 'PA', name: 'Panama',               dialCode: '+507',  flag: '🇵🇦', digitCount: 8  },
  { code: 'PG', name: 'Papua New Guinea',     dialCode: '+675',  flag: '🇵🇬', digitCount: 8  },
  { code: 'PY', name: 'Paraguay',             dialCode: '+595',  flag: '🇵🇾', digitCount: 9  },
  { code: 'PE', name: 'Peru',                 dialCode: '+51',   flag: '🇵🇪', digitCount: 9  },
  { code: 'PH', name: 'Philippines',          dialCode: '+63',   flag: '🇵🇭', digitCount: 10 },
  { code: 'PL', name: 'Poland',               dialCode: '+48',   flag: '🇵🇱', digitCount: 9  },
  { code: 'PT', name: 'Portugal',             dialCode: '+351',  flag: '🇵🇹', digitCount: 9  },
  { code: 'QA', name: 'Qatar',                dialCode: '+974',  flag: '🇶🇦', digitCount: 8  },
  { code: 'RO', name: 'Romania',              dialCode: '+40',   flag: '🇷🇴', digitCount: 10 },
  { code: 'RU', name: 'Russia',               dialCode: '+7',    flag: '🇷🇺', digitCount: 10 },
  { code: 'RW', name: 'Rwanda',               dialCode: '+250',  flag: '🇷🇼', digitCount: 9  },
  { code: 'KN', name: 'Saint Kitts & Nevis',  dialCode: '+1869', flag: '🇰🇳', digitCount: 7  },
  { code: 'WS', name: 'Samoa',                dialCode: '+685',  flag: '🇼🇸', digitCount: 7  },
  { code: 'SM', name: 'San Marino',           dialCode: '+378',  flag: '🇸🇲', digitCount: 9  },
  { code: 'ST', name: 'São Tomé & Príncipe',  dialCode: '+239',  flag: '🇸🇹', digitCount: 7  },
  { code: 'SA', name: 'Saudi Arabia',         dialCode: '+966',  flag: '🇸🇦', digitCount: 9  },
  { code: 'SN', name: 'Senegal',              dialCode: '+221',  flag: '🇸🇳', digitCount: 9  },
  { code: 'RS', name: 'Serbia',               dialCode: '+381',  flag: '🇷🇸', digitCount: 9  },
  { code: 'SL', name: 'Sierra Leone',         dialCode: '+232',  flag: '🇸🇱', digitCount: 8  },
  { code: 'SG', name: 'Singapore',            dialCode: '+65',   flag: '🇸🇬', digitCount: 8  },
  { code: 'SK', name: 'Slovakia',             dialCode: '+421',  flag: '🇸🇰', digitCount: 9  },
  { code: 'SI', name: 'Slovenia',             dialCode: '+386',  flag: '🇸🇮', digitCount: 8  },
  { code: 'SB', name: 'Solomon Islands',      dialCode: '+677',  flag: '🇸🇧', digitCount: 7  },
  { code: 'SO', name: 'Somalia',              dialCode: '+252',  flag: '🇸🇴', digitCount: 8  },
  { code: 'ZA', name: 'South Africa',         dialCode: '+27',   flag: '🇿🇦', digitCount: 9  },
  { code: 'SS', name: 'South Sudan',          dialCode: '+211',  flag: '🇸🇸', digitCount: 9  },
  { code: 'ES', name: 'Spain',                dialCode: '+34',   flag: '🇪🇸', digitCount: 9  },
  { code: 'LK', name: 'Sri Lanka',            dialCode: '+94',   flag: '🇱🇰', digitCount: 9  },
  { code: 'SD', name: 'Sudan',                dialCode: '+249',  flag: '🇸🇩', digitCount: 9  },
  { code: 'SR', name: 'Suriname',             dialCode: '+597',  flag: '🇸🇷', digitCount: 7  },
  { code: 'SE', name: 'Sweden',               dialCode: '+46',   flag: '🇸🇪', digitCount: 9  },
  { code: 'CH', name: 'Switzerland',          dialCode: '+41',   flag: '🇨🇭', digitCount: 9  },
  { code: 'SY', name: 'Syria',                dialCode: '+963',  flag: '🇸🇾', digitCount: 9  },
  { code: 'TW', name: 'Taiwan',               dialCode: '+886',  flag: '🇹🇼', digitCount: 9  },
  { code: 'TJ', name: 'Tajikistan',           dialCode: '+992',  flag: '🇹🇯', digitCount: 9  },
  { code: 'TZ', name: 'Tanzania',             dialCode: '+255',  flag: '🇹🇿', digitCount: 9  },
  { code: 'TH', name: 'Thailand',             dialCode: '+66',   flag: '🇹🇭', digitCount: 9  },
  { code: 'TL', name: 'Timor-Leste',          dialCode: '+670',  flag: '🇹🇱', digitCount: 8  },
  { code: 'TG', name: 'Togo',                 dialCode: '+228',  flag: '🇹🇬', digitCount: 8  },
  { code: 'TO', name: 'Tonga',                dialCode: '+676',  flag: '🇹🇴', digitCount: 7  },
  { code: 'TT', name: 'Trinidad & Tobago',    dialCode: '+1868', flag: '🇹🇹', digitCount: 7  },
  { code: 'TN', name: 'Tunisia',              dialCode: '+216',  flag: '🇹🇳', digitCount: 8  },
  { code: 'TR', name: 'Turkey',               dialCode: '+90',   flag: '🇹🇷', digitCount: 10 },
  { code: 'TM', name: 'Turkmenistan',         dialCode: '+993',  flag: '🇹🇲', digitCount: 8  },
  { code: 'TV', name: 'Tuvalu',               dialCode: '+688',  flag: '🇹🇻', digitCount: 6  },
  { code: 'UG', name: 'Uganda',               dialCode: '+256',  flag: '🇺🇬', digitCount: 9  },
  { code: 'UA', name: 'Ukraine',              dialCode: '+380',  flag: '🇺🇦', digitCount: 9  },
  { code: 'AE', name: 'UAE',                  dialCode: '+971',  flag: '🇦🇪', digitCount: 9  },
  { code: 'GB', name: 'United Kingdom',       dialCode: '+44',   flag: '🇬🇧', digitCount: 10 },
  { code: 'US', name: 'United States',        dialCode: '+1',    flag: '🇺🇸', digitCount: 10 },
  { code: 'UY', name: 'Uruguay',              dialCode: '+598',  flag: '🇺🇾', digitCount: 9  },
  { code: 'UZ', name: 'Uzbekistan',           dialCode: '+998',  flag: '🇺🇿', digitCount: 9  },
  { code: 'VU', name: 'Vanuatu',              dialCode: '+678',  flag: '🇻🇺', digitCount: 7  },
  { code: 'VE', name: 'Venezuela',            dialCode: '+58',   flag: '🇻🇪', digitCount: 10 },
  { code: 'VN', name: 'Vietnam',              dialCode: '+84',   flag: '🇻🇳', digitCount: 10 },
  { code: 'YE', name: 'Yemen',                dialCode: '+967',  flag: '🇾🇪', digitCount: 9  },
  { code: 'ZM', name: 'Zambia',               dialCode: '+260',  flag: '🇿🇲', digitCount: 9  },
  { code: 'ZW', name: 'Zimbabwe',             dialCode: '+263',  flag: '🇿🇼', digitCount: 9  },
];

// ─────────────────────────────────────
//  Sub-Components
// ─────────────────────────────────────

const CountryBox = ({ editable, borderColor, flag, dialCode, pickerOpen, onPress, colors }: any) => (
  <Animated.View style={{ transform: [{ translateX: 0 }] }}>
    <TouchableOpacity
      style={[styles.countryBox, { borderColor, backgroundColor: colors.card }, !editable && { opacity: 0.6 }]}
      onPress={onPress}
      activeOpacity={0.75}
      disabled={!editable}
    >
      <Text style={styles.flag}>{flag}</Text>
      <Text style={[styles.dialCode, { color: colors.ink }]}>{dialCode}</Text>
      <OurIcon name={pickerOpen ? 'chev-u' : 'chev-d'} size={13} color={colors.ink3} />
    </TouchableOpacity>
  </Animated.View>
);

const InputBox = ({ borderColor, isInvalid, color, value, placeholder, digitCount, editable, onFocus, onBlur, onChangeText, touched, isFocused, isValid, colors }: any) => (
  <Animated.View style={[styles.inputBox, { borderColor, backgroundColor: colors.card }]}>
    <TextInput
      style={[styles.input, { color, ...Platform.select({ web: { outlineStyle: 'none', minWidth: 0 } as any }) }]}
      value={value}
      placeholder={placeholder}
      placeholderTextColor={colors.ink4}
      keyboardType="phone-pad"
      editable={editable}
      maxLength={digitCount}
      onFocus={onFocus}
      onBlur={onBlur}
      onChangeText={onChangeText}
    />
    {touched && !isFocused && (
      <View style={styles.statusIcon}>
        {!isValid && <OurIcon name="alert-circle" size={20} color={colors.danger} />}
      </View>
    )}
  </Animated.View>
);

const HintRow = ({ isInvalid, color, error, value, name, digitCount, isValid, accent, green, cardBorder, danger, muted }: any) => {
  let hintLabel = `${name} · ${digitCount} digits`;
  if (error) {
    hintLabel = "";
  } else if (isInvalid) {
    hintLabel = value.length === 0 ? 'Mobile number is required' : `${name} needs ${digitCount} digits (${value.length}/${digitCount})`;
  }

  const getDotBg = (index: number) => {
    if (index >= value.length) return cardBorder;
    if (isInvalid) return danger;
    return isValid ? green : accent;
  };

  return (
    <View style={styles.hintRow}>
      <Text style={[styles.hintText, { color }, isInvalid && styles.hintError]}>
        {hintLabel}
      </Text>
      <View style={styles.dotsRow}>
        {Array.from({ length: Math.min(digitCount, 12) }).map((_, i) => (
          <View key={`dot-${name}-${i}`} style={[styles.dot, { backgroundColor: getDotBg(i) }]} />
        ))}
        {digitCount > 12 && <Text style={{ color: muted, fontSize: 10 }}>+{digitCount - 12}</Text>}
      </View>
    </View>
  );
};


const DROPDOWN_MIN_WIDTH = 300;
const DROPDOWN_MAX_HEIGHT = 340;

/** Country picker anchored directly beneath the country-code box (never a
 *  centered modal), flipping above the box when there isn't room below. */
const CountryPickerDropdown = ({ open, anchor, filteredCountries, onSelect, onClose, search, onSearchChange, selectedCode, colors }: any) => {
  if (!open || !anchor) return null;

  const screen = Dimensions.get('window');
  const width = Math.min(screen.width - 16, Math.max(DROPDOWN_MIN_WIDTH, anchor.width));
  const left = Math.max(8, Math.min(anchor.x, screen.width - width - 8));

  const spaceBelow = screen.height - (anchor.y + anchor.height) - 12;
  const spaceAbove = anchor.y - 12;
  const openUp = spaceBelow < 220 && spaceAbove > spaceBelow;
  const maxHeight = Math.min(DROPDOWN_MAX_HEIGHT, Math.max(180, openUp ? spaceAbove : spaceBelow));
  const position = openUp
    ? { bottom: screen.height - anchor.y + 6 }
    : { top: anchor.y + anchor.height + 6 };

  return (
    <Modal visible={open} transparent animationType="fade" onRequestClose={onClose}>
      {/* Transparent backdrop — tap anywhere outside to close (no dimming) */}
      <TouchableOpacity style={StyleSheet.absoluteFill} activeOpacity={1} onPress={onClose} />
      <View
        style={[
          styles.dropdownPanel,
          position,
          { left, width, maxHeight, borderColor: colors.line, backgroundColor: colors.card },
        ]}
      >
        <View style={[styles.searchRow, { borderBottomColor: colors.line, backgroundColor: colors.paper2 }]}>
          <OurIcon name="search" size={16} color={colors.ink4} />
          <TextInput
            style={[styles.searchInput, { color: colors.ink }, Platform.select({ web: { outlineStyle: 'none' } as any })]}
            placeholder="Search country or code..." placeholderTextColor={colors.ink4} value={search} onChangeText={onSearchChange} autoFocus
          />
          {search.length > 0 && <TouchableOpacity onPress={() => onSearchChange('')}><OurIcon name="x" size={16} color={colors.ink4} /></TouchableOpacity>}
        </View>
        <FlatList
          data={filteredCountries}
          keyExtractor={(item) => item.code}
          keyboardShouldPersistTaps="handled"
          getItemLayout={(_, index) => ({ length: 52, offset: 52 * index, index })}
          renderItem={({ item }) => (
            <TouchableOpacity
              style={[styles.countryRow, { borderBottomColor: colors.line, backgroundColor: colors.card }, item.code === selectedCode && { backgroundColor: colors.accentSoft }]}
              onPress={() => onSelect(item)} activeOpacity={0.7}
            >
              <Text style={styles.rowFlag}>{item.flag}</Text>
              <View style={styles.rowInfo}>
                <Text style={[styles.rowName, { color: colors.ink }]} numberOfLines={1}>{item.name}</Text>
                <Text style={[styles.rowDigits, { color: colors.ink4 }]}>{item.digitCount} digits</Text>
              </View>
              <Text style={[styles.rowDial, { color: colors.accent }]}>{item.dialCode}</Text>
            </TouchableOpacity>
          )}
          ListEmptyComponent={<Text style={{ padding: 16, color: colors.ink4, fontSize: 13 }}>No countries match “{search}”.</Text>}
          style={{ backgroundColor: colors.card }} showsVerticalScrollIndicator={false}
        />
      </View>
    </Modal>
  );
};

// ─────────────────────────────────────
//  Component
// ─────────────────────────────────────

const MobileNumberInput: React.FC<MobileNumberInputProps> = ({
  label,
  required = false,
  value,
  selectedCountry,
  onCountryChange,
  onChangeText,
  onBlur,
  placeholder,
  editable = true,
  error,
  onInvalidChar,
  data,
}) => {
  const { theme } = useTheme();
  const C = theme.colors;

  const resolvedLabel = label ?? data?.label ?? 'MOBILE NUMBER';
  const resolvedValue = value ?? data?.value ?? '';
  const resolvedSelectedCountry = selectedCountry ?? data?.selectedCountry ?? COUNTRIES.find(c => c.code === 'IN')!;
  const resolvedError = error ?? data?.error;

  const [isFocused, setIsFocused]   = useState(false);
  const [touched, setTouched]       = useState(false);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [search, setSearch]         = useState('');
  // Set when the user types a non-digit (special char / letter); cleared on the
  // next digit-only keystroke. An external `error` prop still takes precedence.
  const [specialErr, setSpecialErr] = useState('');
  const [anchor, setAnchor] = useState<{ x: number; y: number; width: number; height: number } | null>(null);

  const shakeAnim = useRef(new Animated.Value(0)).current;
  const countryRef = useRef<View>(null);

  // Measure the country-code box, then open the picker anchored directly beneath it.
  const openPicker = () => {
    if (!editable) return;
    countryRef.current?.measureInWindow((x, y, width, height) => {
      setAnchor({ x, y, width, height });
      setPickerOpen(true);
    });
  };

  const { digitCount } = resolvedSelectedCountry;
  // Optional field: an empty value is valid unless `required`. Any typed digits
  // must still complete the country's digit count.
  const isValid   = resolvedValue.length === 0 ? !required : resolvedValue.length === digitCount;
  const isInvalid = touched && !isFocused && !isValid;

  const triggerShake = () => {
    Animated.sequence([
      Animated.timing(shakeAnim, { toValue: 8,  duration: 55, useNativeDriver: true }),
      Animated.timing(shakeAnim, { toValue: -8, duration: 55, useNativeDriver: true }),
      Animated.timing(shakeAnim, { toValue: 4,  duration: 45, useNativeDriver: true }),
      Animated.timing(shakeAnim, { toValue: 0,  duration: 45, useNativeDriver: true }),
    ]).start();
  };

  const handleBlur = () => {
    setIsFocused(false);
    setTouched(true);
    if (!isValid) triggerShake();
    onBlur?.();
  };

  // External error wins; otherwise surface the special-character error.
  const shownError = resolvedError ?? (specialErr || undefined);

  const getBorderColor = () => {
    if (!editable) return C.line;
    if (shownError) return C.danger;
    if (isFocused) return C.accent;
    if (isInvalid) return C.danger;
    return C.lineStrong;
  };

  const borderColor = getBorderColor();

  const filteredCountries = COUNTRIES.filter(
    (c) => c.name.toLowerCase().includes(search.toLowerCase()) || c.dialCode.includes(search)
  );


  return (
    <View style={styles.container}>
      <Text style={[styles.label, { color: C.ink3 }]}>
        {resolvedLabel}
        {required && <Text style={{ color: C.danger, fontSize: 15 }}> *</Text>}
      </Text>
      <View style={styles.row}>
        <View ref={countryRef} collapsable={false}>
          <CountryBox
            editable={editable} borderColor={borderColor} flag={resolvedSelectedCountry.flag}
            dialCode={resolvedSelectedCountry.dialCode} pickerOpen={pickerOpen}
            onPress={openPicker} colors={C}
          />
        </View>
        <InputBox
          borderColor={borderColor} isInvalid={isInvalid} color={isInvalid ? C.danger : C.ink}
          value={resolvedValue} placeholder={placeholder ?? `Enter a ${digitCount} mobile digit number`}
          digitCount={digitCount} editable={editable} onFocus={() => setIsFocused(true)}
          onBlur={handleBlur}
          onChangeText={(text: string) => {
            const hasBad = /\D/.test(text);
            setSpecialErr(hasBad ? 'Special characters are not allowed' : '');
            if (hasBad) onInvalidChar?.();
            onChangeText?.(text.replace(/\D/g, '').slice(0, digitCount));
          }}
          touched={touched} isFocused={isFocused} isValid={isValid} colors={C}
        />
      </View>

      <HintRow
        isInvalid={isInvalid} color={isInvalid ? C.danger : C.ink3} error={shownError}
        value={resolvedValue} name={resolvedSelectedCountry.name} digitCount={digitCount}
        isValid={isValid} accent={C.accent} green={C.success} cardBorder={C.line}
        danger={C.danger} muted={C.ink3}
      />

      {shownError ? <Text style={[styles.errorText, { color: C.danger }]}>{shownError}</Text> : null}

      <CountryPickerDropdown
        open={pickerOpen} anchor={anchor} filteredCountries={filteredCountries}
        onSelect={(item: CountryEntry) => { onCountryChange?.(item); setPickerOpen(false); setSearch(''); }}
        onClose={() => { setPickerOpen(false); setSearch(''); }} search={search} onSearchChange={setSearch}
        selectedCode={resolvedSelectedCountry.code} colors={C}
      />
    </View>
  );
};

// ─────────────────────────────────────
//  Styles
// ─────────────────────────────────────
const styles = StyleSheet.create({
  container: {
    width: '100%',
  },
  label: {
    fontSize: 12,
    fontWeight: '700',
    letterSpacing: 0.8,
    marginBottom: 8,
    textTransform: 'uppercase',
  },

  // ── Separate box layout with gap ──
  row: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 10,   // ← gap between country code box and phone input box
  },

  countryBox: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    minHeight: 44,
    paddingHorizontal: 12,
    paddingVertical: 6,
    borderWidth: 1.5,
    borderRadius: 2,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.03,
    shadowRadius: 4,
    elevation: 1,
    ...Platform.select({ web: { boxShadow: '0 1px 4px rgba(0,0,0,0.03)' } as any }),
    ...Platform.select({ web: { transition: 'border-color 0.2s ease', boxShadow: '0 1px 2px rgba(0,0,0,0.04)' } as any }),
  },

  flag: {
    fontSize: 18,
    lineHeight: 22,
  },
  dialCode: {
    fontSize: 14,
    fontWeight: '700',
    letterSpacing: 0.3,
    minWidth: 36,
  },

  inputBox: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    minHeight: 44,
    borderWidth: 1.5,
    borderRadius: 2,
    paddingHorizontal: 12,
    paddingVertical: 6,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.03,
    shadowRadius: 4,
    elevation: 1,
    ...Platform.select({ web: { boxShadow: '0 1px 4px rgba(0,0,0,0.03)' } as any }),
    ...Platform.select({ web: { transition: 'border-color 0.2s ease', boxShadow: '0 1px 2px rgba(0,0,0,0.04)', minWidth: 0, overflow: 'hidden' } as any }),
  },

  input: {
    flex: 1,
    fontSize: 14,
    fontWeight: '500',
    letterSpacing: 0.5,
    height: '100%',
  },
  statusIcon: {
    paddingLeft: 6,
  },

  // Hints
  hintRow: {
    flexDirection: 'row',
    alignItems: 'center',
    marginTop: 8,
    gap: 8,
    flexWrap: 'wrap',
  },
  hintText: {
    fontSize: 11,
    flex: 1,
  },
  // Error state — match the bold red error labels used by the other inputs.
  hintError: {
    fontSize: 12,
    fontWeight: '600',
  },
  dotsRow: {
    flexDirection: 'row',
    gap: 3,
    alignItems: 'center',
  },
  dot: {
    width: 5,
    height: 5,
    borderRadius: 3,
  },
  errorText: {
    fontSize: 12,
    fontWeight: '600',
    marginTop: 4,
  },

  // Modal
  // Anchored dropdown panel (opens directly under the country-code box).
  dropdownPanel: {
    position: 'absolute',
    borderRadius: 10,
    borderWidth: 1,
    overflow: 'hidden',
    shadowColor: '#0F172A',
    shadowOffset: { width: 0, height: 12 },
    shadowOpacity: 0.16,
    shadowRadius: 28,
    elevation: 20,
    ...Platform.select({ web: { boxShadow: '0 16px 36px -8px rgba(15,23,42,0.28)' } as any }),
  },
  modalOverlay: {
    flex: 1,
    backgroundColor: 'rgba(0,0,0,0.55)',
    justifyContent: 'center',
    alignItems: 'center',
  },
  pickerPanel: {
    width: 360,
    maxHeight: 540,
    borderRadius: 2,
    borderWidth: 1,
    overflow: 'hidden',
    shadowColor: '#0F172A',
    shadowOffset: { width: 0, height: 12 },
    shadowOpacity: 0.12,
    shadowRadius: 32,
    elevation: 20,
    ...Platform.select({ web: { boxShadow: '0 12px 32px rgba(0,0,0,0.12)' } as any }),
  },
  pickerHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: 18,
    paddingVertical: 14,
    borderBottomWidth: 1,
  },
  pickerTitle: {
    fontSize: 15,
    fontWeight: '800',
  },
  pickerCount: {
    fontSize: 11,
  },
  pickerCloseBtn: {
    width: 28, height: 28, borderRadius: 2,
    justifyContent: 'center', alignItems: 'center',
    borderWidth: 1,
  },
  searchRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    paddingHorizontal: 16,
    paddingVertical: 11,
    borderBottomWidth: 1,
  },
  searchInput: {
    flex: 1,
    fontSize: 14,
    height: 24,
  },
  list: {
    maxHeight: 420,
  },
  countryRow: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 16,
    paddingVertical: 10,
    borderBottomWidth: StyleSheet.hairlineWidth,
    height: 60,
    gap: 12,
  },
  rowFlag: {
    fontSize: 26,
    width: 34,
    textAlign: 'center',
  },
  rowInfo: {
    flex: 1,
  },
  rowName: {
    fontSize: 13,
    fontWeight: '600',
  },
  rowDigits: {
    fontSize: 11,
    marginTop: 1,
  },
  rowDial: {
    fontSize: 13,
    fontWeight: '700',
    minWidth: 44,
    textAlign: 'right',
  },
});

export default MobileNumberInput;